'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface UserProfile {
  id: string;
  company_id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: 'ap_clerk' | 'ap_manager' | 'cfo' | 'controller' | 'owner' | 'admin';
  is_active: boolean;
  is_super_admin?: boolean;
}

interface Company {
  id: string;
  name: string;
  plan: string;
  subscription_status: string | null;
  cash_runway_days: number;
  total_ap_exposure: number;
}

interface AuthContextValue {
  user: any;
  session: any;
  profile: UserProfile | null;
  company: Company | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  isCFO: () => boolean;
  isAPClerk: () => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error || !data) return null;
      setProfile(data as UserProfile);

      if (data.company_id) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('id, name, plan, subscription_status, cash_runway_days, total_ap_exposure')
          .eq('id', data.company_id)
          .single();
        if (companyData) setCompany(companyData as Company);
      }
      return data as UserProfile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setCompany(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://ledgerpay6559.builtwithrocket.new');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
          role: metadata?.role || 'ap_clerk',
          company_name: metadata?.companyName || metadata?.company_name || '',
        },
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setCompany(null);
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const isCFO = () =>
    profile?.role === 'cfo' || profile?.role === 'controller' || profile?.role === 'owner';

  const isAPClerk = () =>
    profile?.role === 'ap_clerk' || profile?.role === 'ap_manager';

  const isAdmin = () => profile?.role === 'admin';

  const isSuperAdmin = () => profile?.is_super_admin === true;

  const value: AuthContextValue = {
    user,
    session,
    profile,
    company,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    isCFO,
    isAPClerk,
    isAdmin,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
